import { Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal, Tooltip } from '@chakra-ui/react';

import type { DefaultToolTipProps } from './types/default-tool-tip.props';

/**
 * Displays a styled tooltip that can optionally render as an interactive popover.
 *
 * By default renders a Chakra `Tooltip`. When `isInteractive` is `true` it renders a
 * `Popover` instead — identical in appearance to allow interactive content.
 *
 * The API is intentionally seamless: all shared behavioral props (`isOpen`, `onOpen`,
 * `onClose`, `placement`, `closeDelay`, etc.) and all styling props work the same way
 * regardless of `isInteractive`. Only props exclusive to `Popover` (e.g. `trigger`,
 * `initialFocusRef`, `returnFocusOnClose`) need to be supplied via `popoverProps`.
 *
 * @see {@link DefaultToolTipProps}
 *
 * @example
 * ```tsx
 * // Non-interactive tooltip (default)
 * <DefaultToolTip label="More info">
 *   <Button>Hover me</Button>
 * </DefaultToolTip>
 *
 * // Interactive — same props, just add isInteractive
 * <DefaultToolTip label={<ScrollableContent />} isInteractive placement="bottom">
 *   <Button>Hover me</Button>
 * </DefaultToolTip>
 *
 * // With Popover-exclusive props
 * <DefaultToolTip label="Details" isInteractive popoverProps={{ returnFocusOnClose: false }}>
 *   <Button>Open</Button>
 * </DefaultToolTip>
 * ```
 */
export const DefaultToolTip = ({
  bg = 'gray.700',
  children,
  closeDelay = 0,
  color = 'white',
  isDisabled = false,
  label,
  p: padding = 2,
  placement = 'auto',
  ...props
}: DefaultToolTipProps) => {
  if (isDisabled) {
    return children;
  }

  if (props.isInteractive) {
    const {
      arrowPadding,
      arrowShadowColor,
      arrowSize = 10,
      closeOnEsc,
      defaultIsOpen,
      direction,
      gutter,
      id,
      isInteractive,
      isOpen,
      modifiers,
      offset,
      onClose,
      onOpen,
      openDelay,
      popoverProps,
      ...contentProps
    } = props;

    const { trigger = 'click', ...rest } = popoverProps ?? {};

    return (
      <Popover
        arrowPadding={arrowPadding}
        arrowShadowColor={arrowShadowColor}
        arrowSize={arrowSize}
        closeDelay={closeDelay}
        closeOnEsc={closeOnEsc}
        defaultIsOpen={defaultIsOpen}
        direction={direction}
        gutter={gutter}
        id={id}
        isLazy
        isOpen={isOpen}
        modifiers={modifiers}
        offset={offset}
        onClose={onClose}
        onOpen={onOpen}
        openDelay={openDelay}
        placement={placement}
        trigger={trigger}
        {...rest}>
        <PopoverTrigger>{children}</PopoverTrigger>
        <Portal>
          <PopoverContent
            _focus={{ boxShadow: 'none' }}
            bg={bg}
            border="none"
            color={color as string | undefined}
            outline="none"
            p={padding}
            pointerEvents="all"
            rounded="xl"
            {...contentProps}>
            <PopoverArrow bg={bg} />
            <PopoverBody>{label}</PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  }

  const { isInteractive, ...tooltipProps } = props;

  return (
    <Tooltip
      bg={bg}
      closeDelay={closeDelay}
      color={color}
      hasArrow
      isDisabled={isDisabled}
      label={label}
      p={padding}
      placement={placement}
      pointerEvents="all"
      rounded="xl"
      {...tooltipProps}>
      {children}
    </Tooltip>
  );
};
